/**
 * Notif Bank — message catalog from "PWA PROJECT MAP - Notif Bank.csv"
 *
 * Categories:
 * - timing: time-of-day hydration nudges (used by scheduled push + carousel)
 * - activity: context / event-based nudges
 * - health: health-benefit education nudges
 * - milestone: streak / progress / recovery nudges
 */

export type NotifTrigger = 'timing' | 'activity' | 'health' | 'milestone';

export type TimingSlot =
  | 'morning_start'
  | 'mid_morning'
  | 'pre_lunch'
  | 'post_lunch'
  | 'afternoon_slump'
  | 'late_afternoon'
  | 'evening_checkout'
  | 'pre_bed';

export type MilestoneKind = 'milestone' | 'perfect_streak' | 'missed_2_plus';

export interface NotifBankEntry {
  trigger: NotifTrigger;
  /** Human-readable category from the CSV */
  category: string;
  /** Stable machine key */
  key: string;
  messages: readonly string[];
}

export interface SelectedNotifMessage {
  text: string;
  icon: string;
  trigger: NotifTrigger;
  category: string;
  key: string;
  variantIndex: number;
}

/** Extract a display icon from message text (first emoji-like grapheme cluster, else water drop). */
export function extractMessageIcon(text: string): string {
  const match = text.match(
    /(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:\uFE0F|\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}))*/u
  );
  return match?.[0] ?? '💧';
}

function entry(
  trigger: NotifTrigger,
  category: string,
  key: string,
  messages: string[]
): NotifBankEntry {
  return { trigger, category, key, messages: messages.filter((m) => m && m !== '-') };
}

// ============================================================================
// TIMING BASED
// ============================================================================

export const TIMING_BANK: readonly NotifBankEntry[] = [
  entry('timing', 'Morning Start', 'morning_start', [
    'Rise & hydrate ☀️',
    'Your brain is 73% water. Feed it. 🧠',
    'Morning magic starts with H2O ✨',
    'Rehydrate overnight losses. Drink up! 💧',
  ]),
  entry('timing', 'Mid-Morning', 'mid_morning', [
    'Focus fuel incoming 🎯',
    'Brain fog? Water fixes that 💨',
    'Sip now, thank yourself later 👍',
    'Cognitive boost = 150ml 🚀',
  ]),
  entry('timing', 'Pre-Lunch', 'pre_lunch', [
    'Hydrated = eat 30% less 🍽️',
    'Drink first. Menu later 💭',
    'Thirst disguises as hunger 🤔',
    'Pre-meal hydration hack 💡',
  ]),
  entry('timing', 'Post-Lunch', 'post_lunch', [
    'Lunch. 30 mins. Then hydrate ⏰',
    'Digestion mode: Water in 30 🕐',
    'Let food settle, then drink 🍴',
    'Post-meal pause, then sip ⏳',
  ]),
  entry('timing', 'Afternoon Slump', 'afternoon_slump', [
    'Ditch the crash. Drink instead ⚡',
    'Fatigue = dehydration signal 😴',
    'Energy reset: 200ml now 🔋',
    'Skip coffee. Try water first ☕→💧',
  ]),
  entry('timing', 'Late Afternoon', 'late_afternoon', [
    'Final stretch fuel ⏱️',
    'Almost done. Stay sharp 🎯',
    'Homestretch hydration 🏁',
    'Evening energy boost 💪',
  ]),
  entry('timing', 'Evening Checkout', 'evening_checkout', [
    'Check out from work, not water 🚪',
    "Day's done. One more sip 📤",
    "Don't ghost your bottle now 👻",
    'Finish strong 💜',
  ]),
  entry('timing', 'Pre-Bed', 'pre_bed', [
    'Almost there. Keep sipping 🌙',
    'Last call (for water) 🕘',
    'Night hydration = morning energy ✨',
    "Tomorrow's you will thank you 🙏",
  ]),
] as const;

// ============================================================================
// ACTIVITY BASED
// ============================================================================

export const ACTIVITY_BANK: readonly NotifBankEntry[] = [
  entry('activity', 'Post-Work', 'post_work', [
    'Workout done? Water next 🛋️',
    'HBO + H2O = better 📺💧',
    'Evening ritual: Hydrate 🌙',
    'Wind down, water up 🌆',
  ]),
  entry('activity', 'Before Meeting', 'before_meeting', [
    'Sharp mind needs water 🎤',
    'Pre-meeting brain boost 💼',
    'Clarity in a glass 🥤',
    'Meeting-ready = hydrated 📊',
  ]),
  entry('activity', 'Before Workout', 'before_workout', [
    'Pre-sweat prep 💪',
    'Fuel up before you move 🏃',
    'Hydrate before you dominate 🔥',
    'Performance starts here 🎯',
  ]),
  entry('activity', 'After Workout', 'after_workout', [
    'Replace what you lost 💦',
    'Sweat replenishment time 🏋️',
    'Recovery = rehydration 🔄',
    'Well done. Now drink up 🏆',
  ]),
  entry('activity', 'Focus Work', 'focus_work', [
    'Focus fuel ⚡',
    'Deep work = deep hydration 🎯',
    'Flow state assistant 💻',
    'Concentration companion 🧠',
    'Brain power: Drink 🧠',
  ]),
  entry('activity', 'Stress Moment', 'stress_moment', [
    'Stress sip 😮‍💨',
    'Calm starts with water 🧘',
    'Breathe. Hydrate. Continue 💙',
    'Anxiety antidote 💧',
  ]),
  entry('activity', 'Headache Alert', 'headache_alert', [
    'Headache? Try water first 🤕',
    'Pain = possible dehydration ⚠️',
    'Natural painkiller: H2O 💊💧',
    'Before meds, try this 💡',
  ]),
  entry('activity', 'Fatigue Signal', 'fatigue_signal', [
    'Tired ≠ lazy. Dehydrated = tired 😴',
    'Energy crisis? Drink 🔋',
    'Fatigue fix: 200ml 💤→⚡',
    'Wake up (without coffee) ☕❌💧',
  ]),
  entry('activity', 'Brain Fog', 'brain_fog', [
    'Fog lifter: Water 🌫️→☀️',
    'Clarity costs 150ml 💭',
    'Mental reset button 🔄',
    'Thoughts stuck? Hydrate 🧠',
  ]),
  entry('activity', 'Hunger Pang', 'hunger_pang', [
    'Real hunger or fake thirst? 🤔',
    'Drink first, snack later 🍪⏸️',
    'Thirst wears a hunger mask 🎭',
    'The 10-min hunger test 💧⏱️',
  ]),
  entry('activity', 'Snack Craving', 'snack_craving', [
    'Craving = thirst 80% of time 🍫❓',
    'Before you snack, hydrate 💧🍿',
    'False alarm test: Drink 💡',
    'Decode the craving 🔍',
  ]),
  entry('activity', 'Pre-Nap', 'pre_nap', [
    'Nap prep: Hydrate 😴💧',
    'Power nap power-up 🔋',
    'Sleep smarter, not harder 🛌',
    'Pre-rest ritual ✨',
  ]),
  entry('activity', 'Post-Nap', 'post_nap', [
    'Wake up right 😊',
    'Refresh + rehydrate ☀️',
    'Nap complete. Water next 🎯',
    'Second wind incoming 💨',
  ]),
  entry('activity', 'Long Call/Meeting', 'long_call_meeting', [
    'Meeting marathon hydration 📞💧',
    'Talk time = sip time 🎤',
    'Voice care = water 🗣️💦',
    'Long call companion 💼',
  ]),
  entry('activity', 'Temp Rise', 'temp_rise', [
    'Heat alert 🌡️⬆️',
    'Summer sipping essential ☀️',
    'Sweat replacement time 💦',
    'Hot day = more H2O 🔥',
  ]),
  entry('activity', 'Caffeine Tax', 'caffeine_tax', [
    'Coffee dehydrates. Balance it ☕💧',
    'Caffeine tax: Pay in water 💰💧',
    '1 coffee = 2 glasses water ☕→💧💧',
    'Caffeinated? Compensate 🔄',
  ]),
  entry('activity', 'Screen Time', 'screen_time', [
    'Screen strain? Hydrate 👀💧',
    'Digital detox drink 💻💦',
    'Blue light break: Water 📱→💧',
    'Eye care starts with H2O 👁️',
  ]),
  entry('activity', 'Commute Time', 'commute_time', [
    'Travel hydration 🚗💧',
    'Road trip essential 🛣️',
    'Journey juice (water) ✈️💦',
    'Commute companion 🚆',
  ]),
  entry('activity', 'Rainy Day', 'rainy_day', [
    'Rain outside ≠ skip inside ☔💧',
    'Monsoon hydration matters 🌧️',
    'Wet weather, dry body 💦',
    'Cozy + hydrated = perfect ☕💧',
  ]),
  entry('activity', 'Sunny Day', 'sunny_day', [
    'Sunshine sipping ☀️💧',
    'UV + heat = more H2O 🌞',
    'Summer essential ⛱️',
    'Bright day = bright hydration 🌤️',
  ]),
  entry('activity', 'Alcohol Intake', 'alcohol_intake', [
    '1:1 rule (1 drink = 1 water) 🍺💧',
    'Hangover prevention mode 🎉💧',
    'Cheers, but hydrate too 🥂💦',
    'Party smart 🍻💡',
  ]),
  entry('activity', 'Moods', 'moods', [
    'Mood lift? Sip up ✨',
    'Boost energy: H2O 🔋',
    'Stressed? Sip H2O 🧘',
  ]),
  entry('activity', 'Meeting', 'meeting', ['Meeting? Sip first 🎤', 'Talking? Sip now 🗣️']),
  entry('activity', 'Food Interaction', 'food_interaction', [
    'Protein? Add water 🥩',
    'Sugary drink? Hydrate 🍭',
    'Spicy food needs more water 🍟',
  ]),
  entry('activity', 'Gym', 'gym', ['Cramp? Drink up 🛠️', 'Tired? Water+electrolytes 😴']),
] as const;

// ============================================================================
// HEALTH
// ============================================================================

export const HEALTH_BANK: readonly NotifBankEntry[] = [
  entry('health', 'Kidney Health', 'kidney_health', [
    'Protect your filters 🛡️',
    'Flush the toxins 💧',
    'Future-proof your organs 🏗️',
    'Renal Rescue: Hydrate to detox 🧪',
  ]),
  entry('health', 'Skin and Lips', 'skin_and_lips', [
    'Dry Lips = Internal Drought 🌵',
    'Skin Elasticity: Water moisturizer 🧴',
    "Nature's Botox: H2O 💧👶",
    'Chapped lips? Water first 👄⚠️',
    'Want glowing skin? Drink ✨',
  ]),
  entry('health', 'Muscle Health', 'muscle_health', [
    'Anti-Spasm Protocol - Hydration',
    'Twitchy muscle? Add water 🦵💡',
    'Performance Lube: Hydrate 🏃‍♂️💨',
    'Muscles need "H2O" 🛠️',
  ]),
  entry('health', 'Metabolic', 'metabolic', [
    'Burn better with H2O 🏃‍♀️',
    'Burn calories while sitting 💧🔥',
    'The $0 weight-loss hack 💸❌',
  ]),
  entry('health', 'Digestion', 'digestion', [
    'Bloat Buster: Drink more 🎈⬇️',
    'Gut health starts with H2O 🛡️',
  ]),
  entry('health', 'Cellular', 'cellular', [
    "ATP's best friend 🧬⚡",
    'Powering 37 trillion cells 🔋💪',
  ]),
  entry('health', 'Joints', 'joints', ['Lubricate & move. 🏃‍♂️']),
  entry('health', 'Toxicity', 'toxicity', ['Deep clean toxic inside. 🧼']),
  entry('health', 'Lipid Support', 'lipid_support', ['Burn fat better. 🔥']),
  entry('health', 'Minerals', 'minerals', ['Hydration helps absorb minerals 💎']),
] as const;

// ============================================================================
// MILESTONE BASED
// ============================================================================

export const MILESTONE_BANK: readonly NotifBankEntry[] = [
  entry('milestone', 'Milestone', 'milestone', [
    'Week 1: Crushing it 💪',
    'Halfway hero 🎉',
    '21 days = habit locked 🔒✨',
    'Progress = perfection 📈',
  ]),
  entry('milestone', 'Perfect Streak', 'perfect_streak', [
    'Legend status 🏆',
    'Purple perfection 💜✨',
    'Habit champion 👑',
    "You're unstoppable 🔥",
  ]),
  entry('milestone', 'Missed 2+', 'missed_2_plus', [
    'Houston, we have a problem 🚨',
    'Catch-up time 💜',
    'Back on track. Drink now 🔄',
    'Recovery mode activated ⚠️',
  ]),
] as const;

export const NOTIF_BANK: readonly NotifBankEntry[] = [
  ...TIMING_BANK,
  ...ACTIVITY_BANK,
  ...HEALTH_BANK,
  ...MILESTONE_BANK,
] as const;

const TIMING_BY_KEY = Object.fromEntries(TIMING_BANK.map((e) => [e.key, e])) as Record<
  TimingSlot,
  NotifBankEntry
>;

const MILESTONE_BY_KEY = Object.fromEntries(MILESTONE_BANK.map((e) => [e.key, e])) as Record<
  MilestoneKind,
  NotifBankEntry
>;

/**
 * Map local clock time to a timing slot from the Notif Bank.
 * Windows cover a typical waking day; night hours map to Pre-Bed.
 */
export function getTimingSlot(hour: number, minute = 0): TimingSlot {
  const t = hour + minute / 60;
  if (t >= 5 && t < 9) return 'morning_start';
  if (t >= 9 && t < 11) return 'mid_morning';
  if (t >= 11 && t < 12.5) return 'pre_lunch';
  if (t >= 12.5 && t < 14) return 'post_lunch';
  if (t >= 14 && t < 16) return 'afternoon_slump';
  if (t >= 16 && t < 17.5) return 'late_afternoon';
  if (t >= 17.5 && t < 20) return 'evening_checkout';
  return 'pre_bed';
}

/** Local hour/minute in an IANA timezone (falls back to host local time). */
export function getLocalTimeParts(
  date: Date = new Date(),
  timezone = 'Asia/Kolkata'
): { hour: number; minute: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? date.getHours());
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? date.getMinutes());
    // en-GB can emit "24" for midnight in some engines
    return { hour: hour === 24 ? 0 : hour, minute };
  } catch {
    return { hour: date.getHours(), minute: date.getMinutes() };
  }
}

function pickFromEntry(bankEntry: NotifBankEntry, reminderNumber: number): SelectedNotifMessage {
  const messages = bankEntry.messages;
  if (messages.length === 0) {
    return {
      text: 'Stay hydrated! 💧',
      icon: '💧',
      trigger: bankEntry.trigger,
      category: bankEntry.category,
      key: bankEntry.key,
      variantIndex: 0,
    };
  }
  const safeReminder = Math.max(1, reminderNumber || 1);
  const variantIndex = (safeReminder - 1) % messages.length;
  const text = messages[variantIndex];
  return {
    text,
    icon: extractMessageIcon(text),
    trigger: bankEntry.trigger,
    category: bankEntry.category,
    key: bankEntry.key,
    variantIndex,
  };
}

/**
 * Scheduled hydration reminder copy: timing slot from clock + variant from reminder number.
 * Client carousel and OneSignal push should call this with the same reminderNumber + timezone.
 */
export function getHydrationReminderMessage(
  reminderNumber: number,
  options?: { timezone?: string; now?: Date }
): SelectedNotifMessage {
  const timezone = options?.timezone ?? 'Asia/Kolkata';
  const now = options?.now ?? new Date();
  const { hour, minute } = getLocalTimeParts(now, timezone);
  const slot = getTimingSlot(hour, minute);
  return pickFromEntry(TIMING_BY_KEY[slot], reminderNumber);
}

const SUPPLEMENTAL_MESSAGE_POOL: readonly NotifBankEntry[] = [...ACTIVITY_BANK, ...HEALTH_BANK];

/**
 * Broader rotation for push + in-app notification copy.
 * Combines reminderNumber with rotationSalt (e.g. successfulIntakes) so messages change
 * even when reminderCount is unchanged (common after logging water from a notification).
 */
export function getRotatingNotificationMessage(
  reminderNumber: number,
  options?: { timezone?: string; now?: Date; rotationSalt?: number }
): SelectedNotifMessage {
  const timezone = options?.timezone ?? 'Asia/Kolkata';
  const now = options?.now ?? new Date();
  const salt = options?.rotationSalt ?? 0;
  const rotationKey = Math.max(1, reminderNumber || 1) + salt;

  const { hour, minute } = getLocalTimeParts(now, timezone);
  const slot = getTimingSlot(hour, minute);
  const timingEntry = TIMING_BY_KEY[slot];

  // Mostly time-of-day messages; periodically surface activity/health bank entries
  if (rotationKey % 3 !== 0) {
    return pickFromEntry(timingEntry, rotationKey);
  }

  const entryIndex = (rotationKey - 1) % SUPPLEMENTAL_MESSAGE_POOL.length;
  return pickFromEntry(SUPPLEMENTAL_MESSAGE_POOL[entryIndex], rotationKey);
}

export function getActivityMessage(key: string, reminderNumber = 1): SelectedNotifMessage | null {
  const bankEntry = ACTIVITY_BANK.find((e) => e.key === key);
  return bankEntry ? pickFromEntry(bankEntry, reminderNumber) : null;
}

export function getHealthMessage(key: string, reminderNumber = 1): SelectedNotifMessage | null {
  const bankEntry = HEALTH_BANK.find((e) => e.key === key);
  return bankEntry ? pickFromEntry(bankEntry, reminderNumber) : null;
}

export function getMilestoneMessage(kind: MilestoneKind, reminderNumber = 1): SelectedNotifMessage {
  return pickFromEntry(MILESTONE_BY_KEY[kind], reminderNumber);
}

/** Flat timing messages for simple rotating UIs / backward compatibility. */
export const HYDRATION_MESSAGES = TIMING_BANK.flatMap((slot) =>
  slot.messages.map((text) => ({
    text,
    icon: extractMessageIcon(text),
    category: slot.category,
    key: slot.key,
  }))
);

export type HydrationMessage = (typeof HYDRATION_MESSAGES)[number];

export function getBankEntry(trigger: NotifTrigger, key: string): NotifBankEntry | undefined {
  return NOTIF_BANK.find((e) => e.trigger === trigger && e.key === key);
}
